import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Image,
  VStack,
  HStack,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import ModernNavbar from '../component/header/ModernNavbar';

// Create a proper motion wrapper that doesn't pass motion props to DOM
const MotionWrapper = ({ children, ...motionProps }) => {
  return (
    <motion.div {...motionProps}>
      {children}
    </motion.div>
  );
};

const ModernGallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const bgColor = useColorModeValue('white', 'neutral.900');
  const textColor = useColorModeValue('neutral.900', 'white');
  const cardBg = useColorModeValue('white', 'neutral.800');

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 },
  };

  const galleryImages = [
    '/assets/images/singha/floor1/IMG_7820.webp',
    '/assets/images/singha/floor1/IMG_7825.webp',
    '/assets/images/singha/floor1/IMG_7828.webp',
    '/assets/images/singha/floor1/IMG_7829.webp',
    '/assets/images/singha/floor1/IMG_7830.webp',
    '/assets/images/singha/floor1/IMG_7833.webp',
    '/assets/images/singha/floor1/IMG_7837.webp',
    '/assets/images/singha/floor1/IMG_7838.webp',
    '/assets/images/singha/floor1/IMG_7839.webp',
    '/assets/images/singha/floor1/IMG_7840.webp',
    '/assets/images/singha/floor1/IMG_7841.webp',
    '/assets/images/singha/floor1/IMG_7842.webp',
  ];

  const handleImageClick = (image) => {
    setSelectedImage(image);
    onOpen();
  };

  return (
    <Box>
      <ModernNavbar />
      
      {/* Hero Section */}
      <Box
        bgImage="url('/assets/images/singha/floor1/IMG_7829.webp')"
        bgSize="cover"
        bgPosition="center"
        bgRepeat="no-repeat"
        position="relative"
        py={20}
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          w="100%"
          h="100%"
          bg="rgba(36, 36, 36, 0.5)"
        />
        <Container maxW="1200px" position="relative" zIndex={2}>
          <MotionWrapper
           
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={6} textAlign="center" color="white">
              <Heading
                as="h1"
                size="2xl"
                fontFamily="heading"
                fontWeight="normal"
              >
                แกลเลอรี่
              </Heading>
              <Text fontSize="xl" maxW="800px" lineHeight="1.6">
                ชมภาพถ่ายและนิทรรศการของพิพิธภัณฑ์สิงห์ท่า 
                ที่บอกเล่าเรื่องราวประวัติศาสตร์และวัฒนธรรมของชุมชนบ้านสิงห์ท่า
              </Text>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Gallery Grid */}
      <Box py={20} bg={bgColor}>
        <Container maxW="1200px">
          <MotionWrapper
           
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={12}>
              <VStack spacing={4} textAlign="center">
                <Heading
                  as="h2"
                  size="xl"
                  fontFamily="heading"
                  color={textColor}
                >
                  ภาพถ่ายนิทรรศการ
                </Heading>
                <Text fontSize="lg" color={textColor} maxW="600px">
                  สำรวจภาพถ่ายที่แสดงนิทรรศการและกิจกรรมต่างๆ 
                  ของพิพิธภัณฑ์สิงห์ท่า คลิกที่ภาพเพื่อดูขนาดใหญ่
                </Text>
              </VStack>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
                {galleryImages.map((image, index) => (
                  <MotionWrapper
                    key={index}
                   
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                  >
                    <Box
                      bg={cardBg}
                      borderRadius="lg"
                      overflow="hidden"
                      boxShadow="lg"
                      cursor="pointer"
                      transition="transform 0.3s ease"
                      _hover={{ transform: 'scale(1.05)' }}
                      onClick={() => handleImageClick(image)}
                    >
                      <Image
                        src={image}
                        alt={`Gallery Image ${index + 1}`}
                        w="100%"
                        h="250px"
                        objectFit="cover"
                      />
                      <Box p={4}>
                        <Text color={textColor} fontSize="sm" textAlign="center">
                          ภาพที่ {index + 1}
                        </Text>
                      </Box>
                    </Box>
                  </MotionWrapper>
                ))}
              </SimpleGrid>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>

      {/* Image Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalCloseButton color="white" zIndex={10} />
          <ModalBody p={0}>
            {selectedImage && (
              <Image
                src={selectedImage}
                alt="Gallery Image"
                w="100%"
                h="auto"
                maxH="90vh"
                objectFit="contain"
                borderRadius="lg"
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Call to Action */}
      <Box py={20} bg="brand.500">
        <Container maxW="1200px">
          <MotionWrapper
           
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={8} textAlign="center" color="white">
              <Heading
                as="h2"
                size="xl"
                fontFamily="heading"
              >
                เยี่ยมชมพิพิธภัณฑ์สิงห์ท่า
              </Heading>
              <Text fontSize="lg" maxW="600px" opacity={0.9}>
                พบกับประสบการณ์การเรียนรู้ที่ไม่มีใครเหมือน 
                ผ่านนิทรรศการที่บอกเล่าเรื่องราวของชุมชนและวัฒนธรรมท้องถิ่น
              </Text>
              <HStack spacing={4}>
                <Button
                  size="lg"
                  bg="white"
                  color="brand.500"
                  _hover={{ bg: 'neutral.50' }}
                  onClick={() => window.location.href = '/contact'}
                >
                  จองเข้าชม
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  borderColor="white"
                  color="white"
                  _hover={{ bg: 'white', color: 'brand.500' }}
                  onClick={() => window.location.href = '/exhibitions'}
                >
                  ดูนิทรรศการ
                </Button>
              </HStack>
            </VStack>
          </MotionWrapper>
        </Container>
      </Box>
    </Box>
  );
};

export default ModernGallery; 